import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";


export const fetchSessionReport = createAsyncThunk(
  "sessionReport/fetchSessionReport",
  async ({ page, limit, startDate, endDate, search, searchType, mobileNumber }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("astroVani-admin-token");

      // Build params object based on searchType
      const params = { page, limit, startDate, endDate };

      // If mobileNumber is provided (from redirect), use it directly
      if (mobileNumber) {
        params.mobileNumber = mobileNumber;
      } else if (search && searchType) {
        // Map searchType to backend parameter names
        const searchParamMap = {
          mobile: 'mobileNumber',
          astrologerName: 'astrologerName',
          userName: 'userName',
          sessionType: 'sessionType'
        };

        const backendParam = searchParamMap[searchType] || 'search';
        params[backendParam] = search;
      } else if (search) {
        // Fallback to generic search if no searchType
        params.search = search;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/sessionReport`,
        {
          params,
          headers: {
            Authorization: token,
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

export const fetchSummaryReport = createAsyncThunk(
  "summaryReport/fetchSummaryReport",
  async ({ page, limit, startDate, endDate, search, searchType, mobileNumber }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("astroVani-admin-token");

      // Build params object based on searchType
      const params = { page, limit, startDate, endDate };

      // If mobileNumber is provided (from redirect), use it directly
      if (mobileNumber) {
        params.mobileNumber = mobileNumber;
      } else if (search && searchType) {
        // Map searchType to backend parameter names
        const searchParamMap = {
          mobile: "mobileNumber",
          astrologerName: "astrologerName",
          userName: "userName",
          sessionType: "sessionType",
        };

        const backendParam = searchParamMap[searchType] || "search";
        params[backendParam] = search;
      } else if (search) {
        // Fallback to generic search if no searchType
        params.search = search;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/summaryReport`,
        {
          params,
          headers: {
            Authorization: token,
          },
        }
      );

      // Normalize response to match existing UI expectations:
      // { data: [], totalCount: number, paginationDetail: { totalRecords: number, ... } }
      const api = response.data || {};
      const rawPagination = api?.paginationDetail || {};
      const totalRecords =
        rawPagination?.totalDocs ??
        rawPagination?.totalRecords ??
        api?.totalCount ??
        api?.totalDocs ??
        0;

      return {
        data: Array.isArray(api?.data) ? api.data : [],
        totalCount: totalRecords,
        paginationDetail: {
          ...rawPagination,
          totalRecords,
          // keep compatibility keys used elsewhere in UI (if needed)
          currentPage: rawPagination?.page ?? rawPagination?.currentPage,
          perPage: rawPagination?.limit ?? rawPagination?.perPage,
          totalPages: rawPagination?.totalPages ?? rawPagination?.totalPages,
        },
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

// Initial state
const initialState = {
  data: [],
  totalCount: 0,
  status: "idle",
  error: null,
  paginationDetail: {
    currentPage: 1,
    perPage: 10,
    totalPages: 0,
    totalRecords: 0,
  },
};

// Slice
const SessionReportReducer = createSlice({
  name: "sessionReport",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSessionReport.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchSessionReport.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.paginationDetail = action.payload.paginationDetail;
        state.error = null;
      })
      .addCase(fetchSessionReport.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(fetchSummaryReport.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchSummaryReport.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.paginationDetail = action.payload.paginationDetail;
        state.error = null;
      })
      .addCase(fetchSummaryReport.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export default SessionReportReducer.reducer;
